/**
 * Pure core for the Home worklist's "Reminders" section (KAN-2555).
 *
 * Everything here is a pure function: no fetching, no clock reads, no
 * navigation. "Today" is always injected by the caller, which is what makes the
 * Today / Past-30 / dropped split testable at its boundaries without fake
 * timers. The hook (`useMyReminders`) is the imperative shell.
 *
 * ⚠️ Dates: a reminder's `dateOn` is a FLOATING CALENDAR DATE, not an instant.
 * `ModalReminderForm` writes it with `Formatter.dateUTCWithoutTime`, i.e. it
 * takes the LOCAL calendar components the coach picked and stamps them at UTC
 * midnight; every read path (the global Reminders table, the modal) renders it
 * back with `timeZone: "UTC"`. So "14 Mar" reads back as "14 Mar" in every
 * timezone. This module therefore compares plain `YYYY-MM-DD` day keys and
 * introduces NO timezone conversion of its own: the reminder's key comes from
 * the value's UTC components, and "today" comes from the coach's LOCAL clock —
 * exactly what the global Reminders page already filters on.
 */

import {
	ITableFilter,
	TableFilterType,
} from "../../../../models/components/table/table-filter.model";
import { IReminder } from "../../../../models/entities/reminder.model";

/**
 * How far back "Past unresolved" reaches, in days, inclusive.
 *
 * Maria (2026-09-16): a cut-off is wanted but the team has not decided whether
 * to also batch-resolve the historic backlog. This is the display-only half —
 * nothing is written, and moving the floor is this one number.
 *
 * INCLUSIVE: a reminder due exactly 30 days before today is shown; 31 days is
 * dropped.
 */
export const PAST_REMINDERS_MAX_AGE_DAYS = 30;

/** The coach's LOCAL calendar day, as `YYYY-MM-DD`. Never `toISOString()`. */
export const localDayKey = (date: Date): string => {
	const year = `${date.getFullYear()}`.padStart(4, "0");
	const month = `${date.getMonth() + 1}`.padStart(2, "0");
	const day = `${date.getDate()}`.padStart(2, "0");
	return `${year}-${month}-${day}`;
};

/** The same day key, shifted by whole days. Handles month/year rollover. */
export const shiftDayKey = (date: Date, days: number): string => {
	const shifted = new Date(date.getFullYear(), date.getMonth(), date.getDate());
	shifted.setDate(shifted.getDate() + days);
	return localDayKey(shifted);
};

/**
 * A reminder's floating due date as `YYYY-MM-DD`, read from the value's UTC
 * components (see the module note). Returns null for a missing/unparseable
 * value so such a row is dropped rather than silently landing in "Today".
 */
export const reminderDueDayKey = (reminder: Pick<IReminder, "dateOn">): string | null => {
	if (!reminder?.dateOn) return null;
	const parsed = new Date(reminder.dateOn);
	if (Number.isNaN(parsed.getTime())) return null;
	const year = `${parsed.getUTCFullYear()}`.padStart(4, "0");
	const month = `${parsed.getUTCMonth() + 1}`.padStart(2, "0");
	const day = `${parsed.getUTCDate()}`.padStart(2, "0");
	return `${year}-${month}-${day}`;
};

/** Display name for a row, and the key the Today group is ordered by. */
export const reminderParticipantName = (
	reminder: Pick<IReminder, "firstName" | "lastName">
): string =>
	[reminder.firstName, reminder.lastName]
		.map((part) => part?.trim())
		.filter(Boolean)
		.join(" ");

export interface GroupedReminders<T> {
	/** Due on the coach's local date, participant name A–Z. */
	today: T[];
	/** Due before today and no older than the floor, oldest (most overdue) first. */
	past: T[];
}

const byName = <T extends Pick<IReminder, "firstName" | "lastName" | "id">>(a: T, b: T): number => {
	const compared = reminderParticipantName(a).localeCompare(
		reminderParticipantName(b),
		undefined,
		{
			sensitivity: "base",
		}
	);
	// Stable tiebreak so two reminders for the same participant never swap
	// between two loads of the same page.
	return compared !== 0 ? compared : (a.id ?? 0) - (b.id ?? 0);
};

/**
 * Split the fetched rows into the two rendered groups (§20.1).
 *
 * Boundaries, stated explicitly because they are the whole point of this
 * function:
 * - due == today               → Today
 * - due == yesterday           → Past
 * - due == today - 30 days     → Past (the floor is INCLUSIVE)
 * - due == today - 31 days     → dropped
 * - due in the FUTURE          → dropped from BOTH groups. Home is "what I have
 *   to do today"; a reminder deliberately dated for next Tuesday is not that,
 *   and showing it under "Today" would misstate its due date. It reappears on
 *   its own day. (The hook also bounds the request, so these rarely arrive.)
 * - missing / unparseable date → dropped
 *
 * `today` is injected; this function never reads the clock.
 */
export const groupReminders = <
	T extends Pick<IReminder, "dateOn" | "firstName" | "lastName" | "id">,
>(
	reminders: T[],
	today: Date
): GroupedReminders<T> => {
	const todayKey = localDayKey(today);
	const floorKey = shiftDayKey(today, -PAST_REMINDERS_MAX_AGE_DAYS);

	const dueToday: T[] = [];
	const past: Array<{ reminder: T; key: string }> = [];

	(reminders ?? []).forEach((reminder) => {
		const key = reminderDueDayKey(reminder);
		if (!key) return;
		if (key === todayKey) dueToday.push(reminder);
		else if (key < todayKey && key >= floorKey) past.push({ reminder, key });
	});

	return {
		today: dueToday.sort(byName),
		past: past
			.sort((a, b) =>
				a.key < b.key ? -1 : a.key > b.key ? 1 : byName(a.reminder, b.reminder)
			)
			.map((entry) => entry.reminder),
	};
};

/**
 * The filter set for "reminders assigned to me", for `GET /api/reminders/`.
 *
 * - `assignedTo == me` is applied UNCONDITIONALLY, including in admin view —
 *   Maria's rule on KAN-2553: the worklist is always "what *I* have to
 *   complete". Do NOT copy the `if (!isAdminViewMode())` guard the global
 *   ParticipantsReminders page puts on its `coachId` filter.
 * - `dateOn` is bounded to [today - PAST_REMINDERS_MAX_AGE_DAYS, today] so the
 *   30-day floor and the future exclusion are enforced server-side too, not
 *   only in `groupReminders`. Both bounds are the floating-date values the rest
 *   of the app writes (local calendar components stamped at UTC midnight).
 * - `resolved` is not sent: the backend query hardcodes `AND resolved = false`.
 *
 * ⚠️ The server ADDS its own `coach_id == me` panel filter for the COACH role
 * (AuthorizationChecker), so v1 really shows "assigned to me AND on my panel".
 * Widening that to assignment-only is a gladstone-data-retriever change pending
 * access-control sign-off — see the note on `useMyReminders`.
 */
export const buildMyRemindersFilters = (assignedToId: number, today: Date): ITableFilter[] => {
	const floor = new Date(today.getFullYear(), today.getMonth(), today.getDate());
	floor.setDate(floor.getDate() - PAST_REMINDERS_MAX_AGE_DAYS);

	const toUtcMidnightISO = (date: Date): string =>
		new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString();

	return [
		{
			field: "assignedTo",
			type: TableFilterType.SELECT_MULTIPLE,
			value: [`${assignedToId}`],
		},
		{
			field: "dateOn",
			type: TableFilterType.DATE_RANGE,
			value: [toUtcMidnightISO(floor), toUtcMidnightISO(today)],
		},
	];
};
